import { isDev } from "../../../../base/index.js";
import { castDraft, produce } from "../../../../immer/index.js";
import { createWaitForValidation } from "../createWaitForValidation/createWaitForValidation.js";
function createValidationApi(store, getFormApi, getLogger, getSubscribersApi, refreshFormStatus) {
  const { waitForValidation, getResolvePendingValidation } = createWaitForValidation(getLogger);
  const { notifyFieldSubscriber, notifyFieldSubscribers, notifyFormSubscribers, notifyMetaSubscribers } = getSubscribersApi();
  let globalValidationRunning = false;
  const validatorsById = /* @__PURE__ */ new Map();
  const validatorIdByFunction = /* @__PURE__ */ new Map();
  const validatorsByField = /* @__PURE__ */ new Map();
  const activeAsyncValidation = /* @__PURE__ */ new Map();
  const runningValidationsByField = /* @__PURE__ */ new Map();
  const errorsByField = /* @__PURE__ */ new Map();
  function updateStoreErrors(validation) {
    const log = getLogger();
    const affectedFieldNames = /* @__PURE__ */ new Set();
    store.errors = produce(store.errors, (draft) => {
      for (const [errorId, formError] of Object.entries(validation)) {
        const currentError = draft[errorId];
        if (formError) {
          affectedFieldNames.add(formError.field);
        } else if (currentError && typeof currentError.field === "string") {
          affectedFieldNames.add(currentError.field);
        }
        if (formError === null) {
          delete draft[errorId];
        } else {
          draft[errorId] = castDraft(formError);
        }
      }
    });
    log("updateStoreErrors", affectedFieldNames);
    for (const fieldName of affectedFieldNames) {
      const errorsList = errorsByField.get(fieldName) ?? [];
      errorsByField.set(
        fieldName,
        produce(errorsList, (draft) => {
          for (const [errorId, value] of Object.entries(validation)) {
            const index = draft.findIndex((err) => err.id === errorId);
            if (index > -1) {
              draft.splice(index, 1);
            }
            if (value !== null && value.field === fieldName) {
              draft.push(castDraft(value));
            }
          }
          draft.sort(({ priority: prev = 0 }, { priority: next = 0 }) => {
            return next - prev;
          });
        })
      );
    }
    return Array.from(affectedFieldNames);
  }
  function refreshFieldsStatus(fieldsToValidate, silent) {
    const log = getLogger();
    log("refreshFieldsStatus", fieldsToValidate);
    const reducedErrors = Object.values(store.errors).reduce((acc, error) => {
      if (fieldsToValidate.includes(error.field)) {
        acc[error.field] ??= true;
      }
      return acc;
    }, {});
    const prevStoreFields = store.fields;
    store.fields = produce(store.fields, (draft) => {
      for (const [fieldName, field] of Object.entries(draft)) {
        if (fieldsToValidate.includes(fieldName)) {
          const hasErrors = !!reducedErrors[fieldName];
          field.status = hasErrors ? "invalid" : "valid";
        }
      }
    });
    let changed = false;
    for (const [fieldName, field] of Object.entries(store.fields)) {
      const prevFieldStatus = prevStoreFields[fieldName]?.status;
      if (field.status !== prevFieldStatus) {
        changed = true;
        if (!silent) {
          notifyFieldSubscriber(fieldName);
        }
      }
    }
    if (changed && !silent) {
      notifyFormSubscribers();
    }
    return changed;
  }
  function refreshFormMetaValidatingState(silent) {
    const validating = globalValidationRunning || Object.values(store.fields).some((field) => field.validating);
    const log = getLogger();
    log("refreshFormMetaValidatingState");
    if (validating !== store.meta.validating) {
      store.meta = produce(store.meta, (draft) => {
        draft.validating = validating;
      });
      if (!silent) {
        notifyMetaSubscribers();
        notifyFormSubscribers();
      }
      return true;
    }
    return false;
  }
  const validationApi = {
    clearValidation: (...userFields) => {
      const log = getLogger();
      log("clearValidation", userFields);
      const targetFields = userFields.length > 0 ? userFields : Object.keys(store.fields);
      store.errors = produce(store.errors, (draft) => {
        for (const id of Object.keys(draft)) {
          const fieldName = draft[id].field;
          if (targetFields.includes(fieldName)) {
            delete draft[id];
            errorsByField.set(fieldName, []);
          }
        }
      });
      store.fields = produce(store.fields, (draft) => {
        for (const [fieldName, field] of Object.entries(draft)) {
          if (targetFields.includes(fieldName)) {
            field.status = "indeterminate";
            notifyFieldSubscriber(fieldName);
          }
        }
      });
      refreshFormStatus();
      notifyFormSubscribers();
    },
    validate: (...userFields) => {
      const log = getLogger();
      log("validate", userFields);
      const formApi = getFormApi();
      const fieldsToValidate = userFields.length > 0 ? userFields : Object.keys(store.fields);
      const fieldsToValidateAndGlobal = [...fieldsToValidate, "global"];
      for (const fieldToValidate of fieldsToValidateAndGlobal) {
        const fieldName = fieldToValidate;
        if (fieldName !== "global" && !store.fields[fieldName]) {
          log(`Trying to validate an unregistered field: ${fieldName}, it will be skipped`);
          continue;
        }
        const runningValidations = runningValidationsByField.get(fieldName) ?? /* @__PURE__ */ new Set();
        runningValidationsByField.set(fieldName, runningValidations);
        const validators = validatorsByField.get(fieldName);
        if (!validators || validators.size === 0) continue;
        const validationTicket = performance.now();
        for (const validator of validators) {
          const validatorFieldName = fieldName === "global" ? void 0 : fieldName;
          const validatorId = validatorIdByFunction.get(validator);
          let syncValidationUnhandledException = false;
          let validation = void 0;
          try {
            validation = validator(formApi, validatorFieldName);
          } catch (reason) {
            if (isDev()) {
              console.warn("Unhandled exception in validation function", reason, {
                validatorId,
                fieldName
              });
            }
            log("Unhandled exception in validation function", reason, {
              validatorId,
              fieldName
            });
            if (fieldName === "global") {
              throw reason;
            }
            const errorMessage = typeof reason === "string" ? reason : reason instanceof Error ? reason.message : "unknown";
            const error = {
              id: validatorId,
              field: fieldName,
              message: errorMessage
            };
            if (validatorId) {
              validation = { [validatorId]: error };
              syncValidationUnhandledException = true;
            }
          }
          if (!validation) continue;
          if (!validatorId) {
            throw new Error(`Impossible to retrieve validator ID for function: ${validator.toString()}`);
          }
          if (validation instanceof Promise) {
            activeAsyncValidation.set(validator, validationTicket);
            runningValidations.add(validatorId);
            if (fieldName === "global") {
              globalValidationRunning = true;
            } else {
              store.fields = produce(store.fields, (draft) => {
                const target = draft[fieldName];
                target.validating = true;
              });
              notifyFieldSubscriber(fieldName);
            }
            refreshFormMetaValidatingState();
            let affectedFields = [];
            validation.then((validationResult) => {
              const latestValidationTicket = activeAsyncValidation.get(validator);
              if (latestValidationTicket !== validationTicket) return;
              if (validationResult) {
                if (isDev() && validationResult.hasOwnProperty(validatorId)) {
                  console.warn(
                    `[Form State] Using a validation function UID as an error ID is a noop. (${validatorId})`
                  );
                }
                const val = {
                  ...validationResult,
                  [validatorId]: null
                };
                affectedFields = updateStoreErrors(val);
              } else {
                const val = { [validatorId]: null };
                affectedFields = updateStoreErrors(val);
              }
              runningValidations.delete(validatorId);
              if (runningValidations.size === 0) {
                if (fieldName === "global") {
                  globalValidationRunning = false;
                } else {
                  store.fields = produce(store.fields, (draft) => {
                    const target = draft[fieldName];
                    target.validating = false;
                  });
                }
              }
              activeAsyncValidation.delete(validator);
              if (fieldName === "global") {
                refreshFieldsStatus(affectedFields, true);
                notifyFieldSubscribers(affectedFields);
              } else {
                refreshFieldsStatus([fieldName], true);
                notifyFieldSubscriber(fieldName);
              }
              refreshFormMetaValidatingState();
              refreshFormStatus();
              notifyFormSubscribers();
              if (!store.meta.validating) {
                getResolvePendingValidation()(store.meta.status === "valid");
              }
            }).catch((reason) => {
              const latestValidationTicket = activeAsyncValidation.get(validator);
              if (latestValidationTicket !== validationTicket) return;
              if (isDev()) {
                console.warn("Unhandled exception in validation function", reason, {
                  validatorId,
                  fieldName
                });
              }
              log("Unhandled exception in validation function", reason, {
                validatorId,
                fieldName
              });
              if (fieldName === "global") {
                throw reason;
              }
              const errorMessage = typeof reason === "string" ? reason : reason instanceof Error ? reason.message : "unknown";
              const error = {
                id: validatorId,
                field: fieldName,
                message: errorMessage
              };
              const affectedFields2 = updateStoreErrors({
                [validatorId]: error
              });
              runningValidations.delete(validatorId);
              if (runningValidations.size === 0) {
                if (fieldName === "global") {
                  globalValidationRunning = false;
                } else {
                  store.fields = produce(store.fields, (draft) => {
                    const field = draft[fieldName];
                    if (field) field.validating = false;
                  });
                }
              }
              activeAsyncValidation.delete(validator);
              if (fieldName === "global") {
                refreshFieldsStatus(affectedFields2, true);
                notifyFieldSubscribers(affectedFields2);
              } else {
                refreshFieldsStatus([fieldName], true);
                notifyFieldSubscriber(fieldName);
              }
              refreshFormMetaValidatingState();
              refreshFormStatus();
              notifyFormSubscribers();
              if (!store.meta.validating) {
                getResolvePendingValidation()(store.meta.status === "valid");
              }
            });
          } else {
            if (isDev() && !syncValidationUnhandledException && validation.hasOwnProperty(validatorId)) {
              console.warn(`[Form State] Using a validation function UID as an error ID is a noop. (${validatorId})`);
            }
            if (!syncValidationUnhandledException) {
              validation = {
                ...validation,
                [validatorId]: null
              };
            }
            const affectedFields = updateStoreErrors(validation);
            if (fieldName === "global") {
              const globalFieldsToNotify = affectedFields.filter((x) => !fieldsToValidate.includes(x)).concat(fieldsToValidate.filter((x) => !affectedFields.includes(x)));
              refreshFieldsStatus(globalFieldsToNotify);
            } else {
              notifyFieldSubscriber(fieldName);
            }
          }
        }
      }
      refreshFieldsStatus(fieldsToValidate);
      refreshFormStatus();
      notifyFormSubscribers();
      return waitForValidation(store.meta);
    },
    waitForValidation: () => {
      const log = getLogger();
      log("waitForValidation");
      return store.meta.validating ? waitForValidation(store.meta).then(() => {
      }) : Promise.resolve();
    },
    getValidator: (id) => {
      return validatorsById.get(id);
    },
    addValidation: (id, validator, fieldName) => {
      const log = getLogger();
      log("addValidation", id, validator, fieldName);
      if (!!fieldName && !store.fields[fieldName]) {
        log(
          `Trying to add validation for an unregistered field: ${fieldName}, it will be skipped at runtime until the field is registered`
        );
      }
      if (validatorsById.has(id)) {
        throw new Error(`Trying to register a validator with ID: ${id} but it's already registered.`);
      }
      validatorsById.set(id, validator);
      validatorIdByFunction.set(validator, id);
      if (fieldName === void 0) {
        validatorsByField.set("global", validatorsByField.get("global") ?? /* @__PURE__ */ new Set());
        validatorsByField.get("global")?.add(validator);
      } else {
        validatorsByField.set(fieldName, validatorsByField.get(fieldName) ?? /* @__PURE__ */ new Set());
        validatorsByField.get(fieldName)?.add(validator);
      }
    },
    removeValidation: (id) => {
      const log = getLogger();
      log("removeValidation", id);
      const validator = validatorsById.get(id);
      validatorsById.delete(id);
      if (!validator) return false;
      validatorIdByFunction.delete(validator);
      for (const [, validators] of validatorsByField) {
        validators.delete(validator);
      }
      return true;
    },
    removeAllValidations: () => {
      const log = getLogger();
      log("removeAllValidations");
      const validations = validatorsById.keys();
      for (const validation of validations) {
        validationApi.removeValidation(validation);
      }
    }
  };
  return {
    errorsByField,
    validatorsById,
    validatorIdByFunction,
    validatorsByField,
    validationApi
  };
}
export {
  createValidationApi
};
//# sourceMappingURL=createValidationApi.js.map
